// Fliken Villkor och texter: villkoren i offerten, hur länge den gäller och
// hur raderna avrundas.
import { useId, useState, type FormEvent } from 'react';
import { lasHeltal, lasKronor, skrivKronor } from '../../katalog/belopp.js';
import { felText } from '../../katalog/useKatalog.js';
import { useKatalogKontext } from './Katalog.js';

export function Villkor() {
  const { katalog, data, kanAndra } = useKatalogKontext();
  const [villkor, setVillkor] = useState(katalog.terms.join('\n'));
  const [dagar, setDagar] = useState(String(katalog.validDays));
  const [avrunda, setAvrunda] = useState(skrivKronor(katalog.roundTo));
  const [fel, setFel] = useState<Record<string, string>>({});
  const [kvitto, setKvitto] = useState(false);
  const id = useId();

  async function spara(e: FormEvent) {
    e.preventDefault();
    setKvitto(false);
    const nyttFel: Record<string, string> = {};
    const giltig = lasHeltal(dagar);
    const steg = lasKronor(avrunda);
    if (giltig === null || giltig < 1) nyttFel.dagar = 'Skriv antal dagar, minst 1';
    if (steg === null || steg < 1) nyttFel.avrunda = 'Skriv ett belopp i kronor, till exempel 5';
    setFel(nyttFel);
    if (Object.keys(nyttFel).length > 0) return;
    try {
      await data.spara.mutateAsync({
        ...katalog,
        terms: villkor
          .split('\n')
          .map((r) => r.trim())
          .filter(Boolean),
        validDays: giltig ?? 30,
        roundTo: steg ?? 100,
      });
      setKvitto(true);
    } catch {
      // Felet visas nedan.
    }
  }

  const beskriv = (falt: string, hjalp?: string) =>
    [hjalp ? `${id}-${falt}-hjalp` : '', fel[falt] ? `${id}-${falt}-fel` : '']
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <form className="kort flikkort" onSubmit={(e) => void spara(e)} noValidate>
      <fieldset className="panel-falt" disabled={!kanAndra || data.spara.isPending}>
        <legend className="visuellt-dold">Villkor och texter</legend>
        <div className="falt">
          <label className="etikett" htmlFor={`${id}-villkor`}>
            Villkor i offerten
          </label>
          <textarea
            id={`${id}-villkor`}
            className="in"
            rows={6}
            aria-describedby={beskriv('villkor', 'ja')}
            value={villkor}
            onChange={(e) => setVillkor(e.target.value)}
          />
          <div className="hjalp" id={`${id}-villkor-hjalp`}>
            Ett villkor per rad. Moms och att priserna är exklusive moms läggs till av offerten.
          </div>
        </div>
        <div className="tva">
          <div className="falt">
            <label className="etikett" htmlFor={`${id}-dagar`}>
              Offerten gäller
            </label>
            <div className="med-enhet">
              <input
                id={`${id}-dagar`}
                className="in mono tal"
                inputMode="numeric"
                aria-invalid={fel.dagar ? true : undefined}
                aria-describedby={beskriv('dagar')}
                value={dagar}
                onChange={(e) => setDagar(e.target.value)}
              />
              <span>dagar</span>
            </div>
            {fel.dagar && (
              <div className="faltfel" id={`${id}-dagar-fel`}>
                {fel.dagar}
              </div>
            )}
          </div>
          <div className="falt">
            <label className="etikett" htmlFor={`${id}-avrunda`}>
              Avrunda raderna till närmaste
            </label>
            <div className="med-enhet">
              <input
                id={`${id}-avrunda`}
                className="in mono tal"
                inputMode="decimal"
                aria-invalid={fel.avrunda ? true : undefined}
                aria-describedby={beskriv('avrunda')}
                value={avrunda}
                onChange={(e) => setAvrunda(e.target.value)}
              />
              <span>kr</span>
            </div>
            {fel.avrunda && (
              <div className="faltfel" id={`${id}-avrunda-fel`}>
                {fel.avrunda}
              </div>
            )}
          </div>
        </div>
        <p className="hjalp">
          Moms: {Math.round(katalog.vatRate * 100)} %. Priserna i katalogen är exklusive moms.
        </p>
      </fieldset>
      {data.spara.isError && (
        <div className="meddelande" role="alert">
          {felText(data.spara.error)}
        </div>
      )}
      <div className="levande" aria-live="polite">
        {kvitto && <div className="kvitto">Villkoren är sparade i utkastet.</div>}
      </div>
      {kanAndra && (
        <div className="panel-fot slut">
          <button type="submit" className="btn btn-s" disabled={data.spara.isPending}>
            {data.spara.isPending ? 'Sparar …' : 'Spara utkast'}
          </button>
        </div>
      )}
    </form>
  );
}
